import { useCallback, useRef, useState } from 'react';
import {
  DocumentContent,
  DocumentMediaType,
  ImageContent,
  ImageMediaType,
} from '../types';
import {
  fileToDocumentContent,
  fileToImageContent,
  isDocumentMediaType,
  isImageMediaType,
  MAX_ATTACHMENTS,
  SUPPORTED_FILE_TYPES,
} from '../utils/attachments';
import { useDragAndDrop } from './useDragAndDrop';

type ImagePreview = {
  id: string;
  dataUrl: string;
  filename: string;
  imageContent: ImageContent;
};

type DocumentPreview = {
  id: string;
  filename: string;
  documentContent: DocumentContent;
};

type PendingAttachment =
  | {
      id: string;
      filename: string;
      kind: 'image';
      mediaType: ImageMediaType;
    }
  | {
      id: string;
      filename: string;
      kind: 'document';
      mediaType: DocumentMediaType;
    };

export type AttachmentPreviewItem =
  | {
      id: string;
      filename: string;
      kind: 'image';
      mediaType: ImageMediaType;
      thumbnailUrl?: string;
      isLoading?: boolean;
    }
  | {
      id: string;
      filename: string;
      kind: 'document';
      mediaType: DocumentMediaType;
      thumbnailUrl?: string;
      isLoading?: boolean;
    };

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

type UseChatAttachmentsOptions = {
  disabled?: boolean;
};

export const useChatAttachments = ({
  disabled = false,
}: UseChatAttachmentsOptions = {}) => {
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [documents, setDocuments] = useState<DocumentPreview[]>([]);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const imagesRef = useRef<ImagePreview[]>([]);
  const documentsRef = useRef<DocumentPreview[]>([]);
  const pendingRef = useRef<PendingAttachment[]>([]);

  imagesRef.current = images;
  documentsRef.current = documents;
  pendingRef.current = pending;

  const total = images.length + documents.length + pending.length;
  const canAddMore = total < MAX_ATTACHMENTS;

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(f => isImageMediaType(f.type));
    const documentFiles = fileArray.filter(f => isDocumentMediaType(f.type));

    if (imageFiles.length === 0 && documentFiles.length === 0) return;

    const currentTotalBefore =
      imagesRef.current.length +
      documentsRef.current.length +
      pendingRef.current.length;
    const availableSlotsBefore = Math.max(
      0,
      MAX_ATTACHMENTS - currentTotalBefore
    );
    if (availableSlotsBefore === 0) return;

    const acceptedImageFiles = imageFiles.slice(0, availableSlotsBefore);
    const remainingSlots = availableSlotsBefore - acceptedImageFiles.length;
    const acceptedDocumentFiles = documentFiles.slice(0, remainingSlots);

    const pendingItems: PendingAttachment[] = [
      ...acceptedImageFiles.map<PendingAttachment>(f => ({
        id: generateId(),
        filename: f.name,
        kind: 'image',
        mediaType: f.type as ImageMediaType,
      })),
      ...acceptedDocumentFiles.map<PendingAttachment>(f => ({
        id: generateId(),
        filename: f.name,
        kind: 'document',
        mediaType: f.type as DocumentMediaType,
      })),
    ];
    const pendingIds = pendingItems.map(p => p.id);

    setPending(prev => [...prev, ...pendingItems]);

    try {
      const imageResults = await Promise.all(
        acceptedImageFiles.map(fileToImageContent)
      );
      const documentResults = await Promise.all(
        acceptedDocumentFiles.map(fileToDocumentContent)
      );

      const newImagePreviews: ImagePreview[] = imageResults
        .filter((result): result is ImageContent => result !== null)
        .map((imageContent, index) => ({
          id: generateId(),
          dataUrl: `data:${imageContent.media_type};base64,${imageContent.data}`,
          filename: imageFiles[index]?.name ?? 'image',
          imageContent,
        }));

      const newDocumentPreviews: DocumentPreview[] = documentResults
        .filter((result): result is DocumentContent => result !== null)
        .map(documentContent => ({
          id: generateId(),
          filename: documentContent.filename,
          documentContent,
        }));

      if (newImagePreviews.length > 0) {
        setImages(prev => [...prev, ...newImagePreviews]);
      }
      if (newDocumentPreviews.length > 0) {
        setDocuments(prev => [...prev, ...newDocumentPreviews]);
      }
    } finally {
      setPending(prev => prev.filter(p => !pendingIds.includes(p.id)));
    }
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const removeDocument = useCallback((id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  }, []);

  const removeAttachment = useCallback(
    (id: string, kind: 'image' | 'document') => {
      if (kind === 'image') {
        removeImage(id);
        return;
      }

      removeDocument(id);
    },
    [removeDocument, removeImage]
  );

  const clear = useCallback(() => {
    setImages([]);
    setDocuments([]);
    setPending([]);
  }, []);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const { items } = event.clipboardData;
      const pastedFiles: File[] = Array.from(items)
        .filter(
          item =>
            item.type.startsWith('image/') || isDocumentMediaType(item.type)
        )
        .map(item => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (pastedFiles.length > 0) {
        event.preventDefault();
        addFiles(pastedFiles);
      }
    },
    [addFiles]
  );

  const { isDragging, dragHandlers } = useDragAndDrop({
    onDrop: addFiles,
    disabled: disabled || !canAddMore,
    accept: SUPPORTED_FILE_TYPES,
  });

  const imageContents =
    images.length > 0 ? images.map(img => img.imageContent) : undefined;
  const documentContents =
    documents.length > 0
      ? documents.map(doc => doc.documentContent)
      : undefined;

  const attachmentPreviews: AttachmentPreviewItem[] = [
    ...images.map<AttachmentPreviewItem>(img => ({
      id: img.id,
      filename: img.filename,
      kind: 'image',
      mediaType: img.imageContent.media_type,
      thumbnailUrl: img.dataUrl,
    })),
    ...documents.map<AttachmentPreviewItem>(doc => ({
      id: doc.id,
      filename: doc.filename,
      kind: 'document',
      mediaType: doc.documentContent.media_type,
    })),
    ...pending.map<AttachmentPreviewItem>(item =>
      item.kind === 'image'
        ? {
            id: item.id,
            filename: item.filename,
            kind: 'image',
            mediaType: item.mediaType,
            isLoading: true,
          }
        : {
            id: item.id,
            filename: item.filename,
            kind: 'document',
            mediaType: item.mediaType,
            isLoading: true,
          }
    ),
  ];

  return {
    attachmentPreviews,
    total,
    canAddMore,
    addFiles,
    removeAttachment,
    clear,
    handlePaste,
    isDragging,
    dragHandlers,
    imageContents,
    documentContents,
  };
};
